/**
 * Nav v2 telemetry — Phase 6.
 *
 * Spec §10 Phase 6: ship `nav_v2_item_clicked`, `nav_v2_command_palette_opened`,
 * `nav_v2_sidebar_collapsed` to existing telemetry.
 *
 * The platform has no dedicated analytics transport (no PostHog / Segment).
 * Sentry is configured with PHI-scrubbing `beforeSend` hooks. Breadcrumbs are
 * the appropriate transport — they ride along with any subsequent error
 * report, give us a free "user journey to error" view, and don't inflate
 * event volume.
 *
 * PHI discipline (CLAUDE.md): NEVER include patient names, clinical
 * narratives, IHI, Medicare, CRN, DOB, diagnoses, addresses, vital signs.
 * Events surface ROUTE SHAPES only — never resolved dynamic segments.
 * `/clients/abc-123` ships as `/clients/[id]`. Use `sanitizeNavRoute()`.
 *
 * Volume discipline: only the three discrete actions named in the spec
 * are instrumented. No per-keystroke / per-render events.
 *
 * SSR-safe: every helper short-circuits when window is undefined or when
 * Sentry isn't loaded. Never throws — instrumentation must not break nav.
 */

import * as Sentry from '@sentry/nextjs';
import { NAV_V2, FOOTER_NAV_V2 } from '@/lib/navigation/nav-v2';
import { SETTINGS_IA } from '@/components/nav-v2/settings/settings-ia';

/**
 * Build the canonical static-routes set from the navigation registries.
 *
 * Codex Phase 6 pass 5 P2 lesson: the multi-iteration position-map +
 * deny-list approach is a losing whack-a-mole game (`/medications/s8-register`,
 * `/clients/new`, `/workflows/closeout`, `/finance/budgets/carry-over`, etc.).
 * The only stable source of truth for "is this a static page or a detail
 * page" is the App Router itself — and for routes wired into navigation,
 * the registry IS the App Router truth.
 *
 * Registry-derived strategy:
 *   1. `KNOWN_STATIC_ROUTES` — every href registered in NAV_V2 +
 *      FOOTER_NAV_V2 (Settings IA is imported below — kept lazy to avoid
 *      hard dependency on icon imports for non-settings consumers).
 *   2. `KNOWN_STATIC_PREFIXES` — every ancestor of a known static route.
 *      Lets us answer "is the path-so-far a registered surface" without
 *      O(n) substring scans.
 *
 * Static-children NOT in the nav (e.g. `/clients/new` — registration
 * flow, no sidebar entry) are covered by a small `STATIC_LEAF_OVERRIDES`
 * deny-list at the end.
 */
const KNOWN_STATIC_ROUTES = new Set<string>();
const KNOWN_STATIC_PREFIXES = new Set<string>();

function registerStaticRoute(href: string) {
  if (!href || !href.startsWith('/')) return;
  KNOWN_STATIC_ROUTES.add(href);
  // Walk every ancestor prefix.
  const segs = href.split('/');
  let acc = '';
  for (let i = 1; i < segs.length; i++) {
    acc = acc + '/' + segs[i];
    KNOWN_STATIC_PREFIXES.add(acc);
  }
}

for (const group of NAV_V2) {
  for (const item of group.items) registerStaticRoute(item.href);
}
for (const item of FOOTER_NAV_V2) registerStaticRoute(item.href);
for (const cluster of SETTINGS_IA) {
  for (const leaf of cluster.items) registerStaticRoute(leaf.href);
}

/**
 * Static App Router leaves that are NOT wired into nav (the user
 * reaches them via deep links, form CTAs, or context-menu actions
 * rather than the sidebar). These need their last segment preserved.
 */
const STATIC_LEAF_OVERRIDES = new Set<string>([
  '/clients/new',
  // Dual-impl 4a–4c: create/bulk-invite and learning leaves live under People.
  '/people/workers/new',
  '/people/workers/bulk-invite',
  '/people/learning/courses',
  '/people/learning/courses/new',
  '/people/learning/analytics',
  '/people/learning/external-evidence',
  '/people/offboarding',
  '/people/development/casual-conversion',
  '/people/compliance/screening',
  '/people/compliance/certifications',
  // Legacy dual-host shapes still appear in historical bookmarks / telemetry
  // until traffic drains; keep sanitisation stable for those path strings.
  '/staff/new',
  '/staff/bulk-invite',
  '/staff/training',
  '/care-plans/new',
  '/incidents/new',
  '/visitors/new',
  '/me/roster',
  '/me/hours',
  '/me/profile',
  '/me/availability',
  '/me/training',
  '/me/onboarding',
  '/settings/integrations/xero',
  '/settings/integrations/xero/backfill',
  '/settings/integrations/myob',
  '/settings/integrations/quickbooks',
  '/settings/integrations/proda',
  '/settings/integrations/mha',
  '/settings/integrations/dhda',
]);
for (const href of STATIC_LEAF_OVERRIDES) registerStaticRoute(href);

export type NavV2EventName =
  | 'nav_v2_item_clicked'
  | 'nav_v2_command_palette_opened'
  | 'nav_v2_sidebar_collapsed';

export type NavV2EventSource =
  | 'sidebar'
  | 'sidebar-collapsed'
  | 'sidebar-pinned'
  | 'sidebar-domain-tiles'
  | 'sidebar-footer'
  | 'mobile-drawer'
  | 'topbar'
  | 'command-palette'
  | 'settings-rail'
  | 'section-tabs'
  | 'shortcut'
  | 'incident-detail';

export interface NavV2EventProperties {
  /** Sanitised route pattern. NEVER raw dynamic segments. */
  route?: string;
  source?: NavV2EventSource;
  /** Toggle direction for `nav_v2_sidebar_collapsed`. */
  collapsed?: boolean;
  /** Low-cardinality discriminator from a fixed enum at the call site. */
  variant?: string;
}

/**
 * Known dynamic-segment positions in the App Router. Anything sitting
 * at `<parent>/{any}/...` collapses to `<parent>/[id]/...` regardless
 * of the segment's shape — protects against short slugs like
 * `abc-123` that wouldn't trip the UUID/integer/opaque-slug heuristic
 * below.
 *
 * Source of truth is the App Router itself (`apps/web/src/app/.../[id]/`).
 * Keep this list in sync when new `[id]` / `[slug]` routes are added.
 */
/**
 * Collapse UUIDs, integer ids, and slug-shaped ids into `[id]` so the
 * resulting key has bounded cardinality and carries no PHI. Preserves
 * human-readable static paths.
 *
 * Strategy is CONTENT-BASED — never position-based. Codex Phase 6
 * passes 3 + 4 walked through three iterations:
 *   - Pass 2 added a `KNOWN_DYNAMIC_PARENTS` position-map.
 *   - Pass 3 corrected: `/clients/new`, `/reports/subscriptions`,
 *     `/workflows/closeout` are static children of dynamic parents.
 *     Added a deny-list.
 *   - Pass 4 corrected again: `/medications/mar`, `/staff/training`,
 *     `/roster/timesheets`, `/finance/budgets/carry-over` (and the
 *     long tail of similar static segments) blow up any deny-list.
 *     Reverted to pure content heuristics.
 *
 * The four content rules below catch every realistic id shape this
 * platform uses (UUIDv4 — Supabase default; integers; nanoid-style
 * opaque slugs; hyphen+digit short slugs like `abc-123`) without
 * misclassifying any static App Router page named with lower-case
 * letters and hyphens (`carry-over`, `data-management`, `mar`,
 * `training`, `timesheets`, etc.) — because none of those carry
 * digits.
 */
function looksLikeId(seg: string): boolean {
  // UUIDv4
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) return true;
  // Integer
  if (/^\d+$/.test(seg)) return true;
  // Opaque slug — 10+ chars with digit
  if (seg.length >= 10 && /[0-9]/.test(seg) && /^[A-Za-z0-9_-]+$/.test(seg)) return true;
  // Hyphen-and-digit short slug — `abc-123`, `X9F-42`
  if (/-/.test(seg) && /[0-9]/.test(seg) && /^[A-Za-z0-9_-]+$/.test(seg)) return true;
  return false;
}

/**
 * Sanitise an App Router pathname for telemetry. Resolution order:
 *
 *   1. Exact-match registered static route → return as-is (covers
 *      `/medications/s8-register`, `/finance/budgets/carry-over`, etc.).
 *   2. Walk segments. At each step, if `path-so-far + '/' + seg` is a
 *      registered static prefix, keep `seg`. Otherwise, if `path-so-far`
 *      itself is a known prefix, treat the next segment as `[id]`
 *      (covers `/clients/abc-123-def-456` → `/clients/[id]`). Otherwise
 *      apply content heuristics (UUID / integer / opaque slug / hyphen-
 *      and-digit) so off-registry routes still get a useful shape.
 *
 * This replaces the multi-pass position-map + deny-list approach
 * (Codex passes 2 → 5) — the registry IS the authoritative source for
 * what's static vs dynamic on this platform.
 */
export function sanitizeNavRoute(route: string | null | undefined): string {
  if (!route) return '/';
  if (route === '/') return '/';
  const noQuery = route.split('?')[0]?.split('#')[0] ?? '/';

  // Fast path: full match against the registry.
  if (KNOWN_STATIC_ROUTES.has(noQuery)) return noQuery;

  const segments = noQuery.split('/');
  const out: string[] = [];
  let pathSoFar = '';
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i] ?? '';
    if (seg === '') {
      out.push('');
      continue;
    }
    const candidate = pathSoFar + '/' + seg;
    if (KNOWN_STATIC_PREFIXES.has(candidate)) {
      // Registered static prefix — keep the segment verbatim.
      out.push(seg);
      pathSoFar = candidate;
      continue;
    }
    // Off-registry territory — apply content heuristics. The
    // "collapse-next-seg-because-pathSoFar-is-registered" rule was
    // dropped after Codex Phase 6 pass 6 P2 — the registry isn't
    // complete enough to be authoritative for negative space (every
    // `command-palette.tsx` NAV_ITEMS entry that isn't in NAV_V2 /
    // SETTINGS_IA would have been mis-collapsed as `[id]`). The
    // content-only fallback below is less aggressive but the trade-off
    // is correct: prefer preserving unknown static pages over
    // aggressively collapsing hypothetical short slugs that, in this
    // platform (Supabase UUIDs everywhere), don't realistically occur
    // as detail-page ids.
    if (looksLikeId(seg)) {
      out.push('[id]');
      pathSoFar = pathSoFar + '/[id]';
      continue;
    }
    out.push(seg);
    pathSoFar = candidate;
  }
  return out.join('/');
}

export function trackNavV2Event(name: NavV2EventName, properties: NavV2EventProperties = {}): void {
  if (typeof window === 'undefined') return;
  try {
    const route = properties.route !== undefined ? sanitizeNavRoute(properties.route) : undefined;
    Sentry.addBreadcrumb({
      category: 'nav-v2',
      type: 'navigation',
      level: 'info',
      message: name,
      data: {
        ...(route !== undefined && { route }),
        ...(properties.source !== undefined && { source: properties.source }),
        ...(properties.collapsed !== undefined && { collapsed: properties.collapsed }),
        ...(properties.variant !== undefined && { variant: properties.variant }),
      },
    });
  } catch {
    // Telemetry failures must not block navigation.
  }
}
